import { Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Location } from "../../../models/location";

import { useAppState } from "../../../context/StateContext";

import Checkbox from "../../Form/Checkbox";
import Input from "../../Form/Input";
import MapInput from "../../Form/MapInput";

import useStyles from "./styles";

type Props = {
  locations?: Location[];
  showHeader?: boolean;
  onChange?: (locations: Location[]) => void;
  viewOnly?: boolean;
};

const ListAddressByType = (props: Props) => {
  const { showHeader, locations, onChange, viewOnly } = props;

  const { currentUser } = useAppState();

  const classes = useStyles();

  const { t: translate } = useTranslation();

  const [indexMainAddress, setIndexMainAddress] = useState<number>();
  const [mainAddress, setMainAddress] = useState<Location>();

  const [indexPostalAddress, setIndexPostalAddress] = useState<number>();
  const [postalAddress, setPostalAddress] = useState<Location>();

  const [useSameAddress, setUseSameAddress] = useState<boolean>(true);

  useEffect(() => {
    getAddressesByArray();
  }, [locations]);

  useEffect(() => {
    if (postalAddress && useSameAddress) setUseSameAddress(false);
  }, [postalAddress]);

  const getAddressesByArray = () => {
    let mainAddress: Location | undefined = undefined;
    let indexMainAddress: number | undefined = undefined;
    let postalAddress: Location | undefined = undefined;
    let indexPostalAddress: number | undefined = undefined;

    locations?.find((location, index) => {
      if (location?.type === "main" && location?.valid) {
        mainAddress = location;
        indexMainAddress = index;
        return true;
      }
    });

    locations?.find((location, index) => {
      if (location?.type === "postal" && location?.valid) {
        postalAddress = location;
        indexPostalAddress = index;
        return true;
      }
    });

    setMainAddress(mainAddress);
    setIndexMainAddress(indexMainAddress);
    setPostalAddress(postalAddress);
    setIndexPostalAddress(indexPostalAddress);
  };

  const handleChange = (location: Location, index?: number) => {
    const locationsUpdated = [...(locations || [])];
    if (index !== undefined) {
      if (locationsUpdated && locationsUpdated?.length > 0 && locationsUpdated[Number(index)]) {
        locationsUpdated[Number(index)] = {
          ...locationsUpdated[Number(index)],
          ...location,
        };
      }
    } else {
      locationsUpdated.push({
        ...location,
        valid: true,
        status: "Active",
        companyId: currentUser?.companyId,
      });
    }
    if (onChange) onChange(locationsUpdated);
  };

  const onDelete = (location: Location, index: number) => {
    if (location?.id) {
      handleChange({ ...location, valid: false }, index);
    } else {
      const locationsUpdated = [...(locations || [])];
      delete locationsUpdated[index];
      if (onChange) onChange(locationsUpdated.filter(Boolean));
    }
  };

  const onChangeSameAddress = (checked: boolean) => {
    setUseSameAddress(checked);
    if (checked) {
      if (postalAddress && indexPostalAddress !== undefined)
        onDelete(postalAddress, indexPostalAddress);
    }
  };

  const renderCheckbox = (show: boolean, disabled?: boolean) => {
    if (show) {
      return (
        <Checkbox
          disabled={disabled}
          control={{
            "data-cy": "checkbox-same-as-main",
          }}
          checked={useSameAddress}
          onChange={(event, checked) => onChangeSameAddress(checked)}
          label={translate("useTheSameAddressForPostalAddress")}
        />
      );
    }
  };

  const renderHeaderLocation = (title: string, showCheckbox: boolean, disabled?: boolean) => {
    return (
      <Grid container item justifyContent="flex-start" direction="column" style={{ gap: "0.3rem" }}>
        <span className={classes.title}>{title}</span>

        {showHeader && renderCheckbox(showCheckbox, disabled)}
      </Grid>
    );
  };

  const renderLocation = (
    title: string,
    showCheckbox: boolean,
    type: string,
    disabled?: boolean,
    index?: number,
    location?: Location
  ) => {
    return (
      <>
        {renderHeaderLocation(title, showCheckbox, disabled)}
        <Grid item xs={12}>
          <MapInput
            location={location}
            onChange={(location) => {
              handleChange({ ...(location as Location), type }, index);
            }}
            label={translate("address")}
            onChangeInput={(value) =>
              handleChange({ ...location, longAddress: value, type }, index)
            }
            inputValue={location?.longAddress}
            placeholder="Google Maps search"
            data-cy={`${type}-address`}
            disabled={disabled}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Input
            label={translate("suburbTown")}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleChange({ ...location, suburb: event.target.value, type }, index)
            }
            value={location?.suburb}
            data-cy={`${type}-suburb-town`}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Input
            label={translate("state")}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleChange({ ...location, state: event.target.value, type }, index)
            }
            value={location?.state}
            data-cy={`${type}-state`}
            disabled={disabled}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Input
            label={translate("postcode")}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleChange({ ...location, postalcode: event.target.value, type }, index)
            }
            value={location?.postalcode}
            data-cy={`${type}-postalcode`}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Input
            label={translate("country")}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleChange({ ...location, country: event.target.value, type }, index)
            }
            value={location?.country}
            data-cy={`${type}-country`}
            disabled={disabled}
          />
        </Grid>
      </>
    );
  };

  const renderPostalAddress = () => {
    if (!useSameAddress && showHeader !== false) {
      return renderLocation(
        translate("postalAddress"),
        false,
        "postal",
        viewOnly,
        indexPostalAddress,
        postalAddress
      );
    }
  };

  return (
    <Grid container alignItems="center" direction="row" spacing={2}>
      {renderLocation(translate("address"), true, "main", viewOnly, indexMainAddress, mainAddress)}
      {renderPostalAddress()}
    </Grid>
  );
};

export default ListAddressByType;
